import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { CertificationService } from '../../core/data-services/certification.service';
import { Certification } from '../../core/data-models/certification.model';
import { CertificationType } from '../../core/data-models/certification-type.model';
import { CERTIFICATION_TYPE_NAMES } from '../../core/utils/certification-utils';
import { CertificationCreateRequest } from '../../core/request-models/certification-create-request.model';
import { CertificationUpdateRequest } from '../../core/request-models/certification-update-request.model';
import { UsStateSelect } from './app-us-state-select/app-us-state-select';
import { FileUploadService } from '../../core/api-services/file-upload.service';

/**
 * Certification Upload Modal Component
 *
 * Modal for uploading certifications to organizations.
 * Handles file upload via S3 pre-signed URLs.
 */
@Component({
  selector: 'app-certification-upload-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, UsStateSelect],
  templateUrl: './certification-upload-modal.component.html',
  styleUrls: ['./certification-upload-modal.component.scss']
})
export class CertificationUploadModalComponent implements OnInit {
  @Input() organizationId?: string;
  @Input() profileId?: string;
  @Input() certification?: Certification;
  @Output() close = new EventEmitter<void>();
  @Output() uploaded = new EventEmitter<void>();

  get isEditMode(): boolean {
    return !!this.certification;
  }

  // Form fields
  certificationType: CertificationType = 'faa-44807';
  certificateNumber = '';
  issuedDate = '';
  expirationDate = '';
  issuingAuthority = '';
  state = '';
  notes = '';

  // File upload
  selectedFile: File | null = null;
  uploadedFileUrl = '';
  uploadedFileName = '';

  // UI state
  uploading = false;
  uploadingFile = false;
  errorMessage: string | null = null;

  certificationTypes: { value: CertificationType; label: string }[] = [];

  // Default issuing authorities by certification type
  private readonly defaultAuthorities: Partial<Record<CertificationType, string>> = {
    'faa-44807': 'FAA',
    'faa-part-107': 'FAA',
    'faa-part-108': 'FAA',
    'faa-part-137': 'FAA',
    'medical': 'FAA',
  };

  constructor(
    private certificationService: CertificationService,
    private fileUploadService: FileUploadService
  ) {}

  ngOnInit(): void {
    // Populate certification types based on owner type
    if (this.organizationId) {
      // Organization certifications
      this.certificationTypes = [
        { value: 'faa-44807', label: CERTIFICATION_TYPE_NAMES['faa-44807'] },
        { value: 'faa-part-137', label: CERTIFICATION_TYPE_NAMES['faa-part-137'] },
        { value: 'insurance', label: CERTIFICATION_TYPE_NAMES['insurance'] },
        { value: 'medical', label: CERTIFICATION_TYPE_NAMES['medical']},
        { value: 'state-applicator', label: CERTIFICATION_TYPE_NAMES['state-applicator'] },
        { value: 'other', label: CERTIFICATION_TYPE_NAMES['other'] }
      ];
    } else if (this.profileId) {
      // Member certifications
      this.certificationTypes = [
        { value: 'faa-part-107', label: CERTIFICATION_TYPE_NAMES['faa-part-107'] },
        { value: 'faa-part-108', label: CERTIFICATION_TYPE_NAMES['faa-part-108'] },
        { value: 'medical', label: CERTIFICATION_TYPE_NAMES['medical'] },
        { value: 'state-applicator', label: CERTIFICATION_TYPE_NAMES['state-applicator'] },
        { value: 'other', label: CERTIFICATION_TYPE_NAMES['other'] }
      ];
      this.certificationType = 'faa-part-107';
    }

    // Pre-populate form in edit mode
    if (this.certification) {
      this.certificationType = this.certification.certificationType ?? this.certificationType;
      this.certificateNumber = this.certification.certificateNumber ?? '';
      this.issuedDate = this.certification.issuedDate?.substring(0, 10) || '';
      this.expirationDate = this.certification.expirationDate?.substring(0, 10) || '';
      this.issuingAuthority = this.certification.issuingAuthority ?? '';
      this.state = this.certification.state || '';
      this.notes = this.certification.notes || '';
      this.uploadedFileUrl = this.certification.documentUrl || '';
      this.uploadedFileName = this.certification.documentFileName || '';
    } else {
      // Pre-fill issuing authority for the default type
      this.onCertificationTypeChange();
    }
  }

  onCertificationTypeChange(): void {
    this.issuingAuthority = this.defaultAuthorities[this.certificationType] || '';
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        this.errorMessage = 'Please upload a PDF, JPEG, or PNG file';
        this.selectedFile = null;
        return;
      }

      // Validate file size (100MB max)
      const maxSize = this.fileUploadService.getMaxFileSize();
      if (file.size > maxSize) {
        this.errorMessage = 'File size must be less than ' + this.fileUploadService.formatFileSize(maxSize);
        this.selectedFile = null;
        return;
      }

      this.selectedFile = file;
      this.errorMessage = null;
    }
  }

  async uploadFile(): Promise<boolean> {
    if (!this.selectedFile) {
      return false;
    }

    this.uploadingFile = true;
    this.errorMessage = '';

    try {
      // Step 1: Get pre-signed URL (signed private, so no ACL header on the PUT)
      const { uploadUrl, fileKey } = await this.fileUploadService.getCertificationUploadUrl(this.selectedFile.name);

      // Step 2: Upload file to S3
      await this.fileUploadService.uploadToSignedUrl(uploadUrl, this.selectedFile);

      // Step 3: Save the S3 key
      this.uploadedFileUrl = fileKey;
      this.uploadedFileName = this.selectedFile.name;

      return true;
    } catch (error: any) {
      console.error('File upload failed:', error);
      this.errorMessage = 'Failed to upload file. Please try again.';
      return false;
    } finally {
      this.uploadingFile = false;
    }
  }

  async onSubmit(): Promise<void> {
    this.errorMessage = '';

    // Validate required fields
    if (!this.certificationType || !this.certificateNumber || !this.issuedDate || !this.issuingAuthority) {
      this.errorMessage = 'Please fill in all required fields.';
      return;
    }

    this.uploading = true;

    try {
      // Upload file if selected
      if (this.selectedFile) {
        const uploaded = await this.uploadFile();
        if (!uploaded) {
          this.uploading = false;
          return;
        }
      }

      if (this.isEditMode && this.certification) {
        // Update existing certification
        // An emptied optional field is sent as null, so the hub clears it
        const updateRequest: CertificationUpdateRequest = {
          certificateNumber: this.certificateNumber,
          issuedDate: this.issuedDate,
          expirationDate: this.expirationDate || null,
          issuingAuthority: this.issuingAuthority,
          state: this.state || null,
          documentUrl: this.uploadedFileUrl || null,
          documentFileName: this.uploadedFileName || null,
          notes: this.notes || null
        };

        await firstValueFrom(
          this.certificationService.updateCertification(this.certification.id, updateRequest)
        );
      } else {
        // Create certification request
        const request: CertificationCreateRequest = {
          certificationType: this.certificationType,
          ownerType: this.organizationId ? 'organization' : 'profile',
          certificateNumber: this.certificateNumber,
          issuedDate: this.issuedDate,
          expirationDate: this.expirationDate || undefined,
          issuingAuthority: this.issuingAuthority,
          state: this.state || undefined,
          documentUrl: this.uploadedFileUrl || undefined,
          documentFileName: this.uploadedFileName || undefined,
          notes: this.notes || undefined
        };

        if (this.organizationId) {
          await firstValueFrom(
            this.certificationService.uploadOrganizationCertification(this.organizationId, request)
          );
        } else if (this.profileId) {
          await firstValueFrom(
            this.certificationService.uploadMemberCertification(this.profileId, request)
          );
        }
      }

      // Emit success and close modal
      this.uploaded.emit();
    } catch (error: any) {
      console.error('Failed to upload certification:', error);
      this.errorMessage = ApiError.fromHttpError(error).errors[0] ?? 'Failed to upload certification. Please try again.';
    } finally {
      this.uploading = false;
    }
  }

  onClose(): void {
    this.close.emit();
  }

  get isValid(): boolean {
    return !!(
      this.certificationType &&
      this.certificateNumber &&
      this.issuedDate &&
      this.issuingAuthority
    );
  }
}
